import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import api from '../lib/api';
import {
  Activity, Server, Cpu, Mic, Brain, Volume2, Database,
  Phone, CheckCircle, Clock, TrendingUp, Zap, Shield, Globe
} from 'lucide-react';

const PIPELINE_STEPS = [
  { id: 'call', icon: Phone, label: 'Chiamata in Entrata', sub: 'Rete Telefonica PSTN', color: 'from-sky-500 to-sky-600', delay: 0 },
  { id: 'gateway', icon: Globe, label: 'TelcaVoIP Gateway', sub: 'SIP/WebSocket Bidirezionale', color: 'from-indigo-500 to-indigo-600', delay: 150 },
  { id: 'stt', icon: Mic, label: 'Riconoscimento Vocale', sub: 'Speech-to-Text Neurale', color: 'from-violet-500 to-violet-600', delay: 300 },
  { id: 'ai', icon: Brain, label: 'Motore AI Conversazionale', sub: 'NLP + Contesto Clinico', color: 'from-fuchsia-500 to-fuchsia-600', delay: 450 },
  { id: 'tts', icon: Volume2, label: 'Sintesi Vocale HD', sub: 'Voce Naturale Italiana', color: 'from-rose-500 to-rose-600', delay: 600 },
  { id: 'response', icon: Phone, label: 'Risposta al Paziente', sub: 'Audio Streaming Real-Time', color: 'from-emerald-500 to-emerald-600', delay: 750 },
];

const SERVICE_MAP = {
  voice_gateway: { label: 'Voice Gateway', icon: Globe, desc: 'Instradamento chiamate SIP in tempo reale' },
  speech_recognition: { label: 'Speech Recognition', icon: Mic, desc: 'Trascrizione vocale con AI neurale multilingua' },
  ai_engine: { label: 'AI Engine', icon: Brain, desc: 'Elaborazione linguaggio naturale e gestione prenotazioni' },
  voice_synthesis: { label: 'Voice Synthesis', icon: Volume2, desc: 'Sintesi vocale HD con 7 voci italiane naturali' },
  database: { label: 'Database Cluster', icon: Database, desc: 'Storage distribuito, crittografato, backup automatici' },
};

export default function Infrastructure() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animStep, setAnimStep] = useState(-1);

  useEffect(() => {
    api.get('/dashboard/infrastructure')
      .then(res => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Animate pipeline on load
  useEffect(() => {
    if (!loading) {
      const interval = setInterval(() => {
        setAnimStep(prev => {
          if (prev >= PIPELINE_STEPS.length) return 0;
          return prev + 1;
        });
      }, 800);
      return () => clearInterval(interval);
    }
  }, [loading]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 max-w-6xl" data-testid="infrastructure-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-xl flex items-center justify-center">
              <Cpu className="w-5 h-5 text-white" />
            </div>
            Infrastruttura AI
          </h1>
          <p className="text-sm text-slate-500 mt-1">Architettura cloud-native per l'assistente vocale del tuo centro medico</p>
        </div>

        {/* Pipeline Visualization */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 overflow-hidden" data-testid="pipeline-section">
          <h2 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <Activity className="w-5 h-5 text-violet-500" />
            Pipeline Vocale AI — Flusso in Tempo Reale
          </h2>
          <p className="text-xs text-slate-400 mb-6">Ogni chiamata attraversa 6 componenti in meno di 2 secondi</p>

          <div className="grid grid-cols-6 gap-2">
            {PIPELINE_STEPS.map((step, i) => {
              const isActive = animStep === i;
              const isPast = animStep > i;
              return (
                <div key={step.id} className="relative" data-testid={`pipeline-${step.id}`}>
                  <div className={`relative rounded-xl p-3 text-center transition-all duration-500 ${
                    isActive ? 'scale-105 shadow-lg ring-2 ring-offset-2 ring-violet-400' :
                    isPast ? 'opacity-60' : 'opacity-40'
                  }`}>
                    <div className={`w-12 h-12 mx-auto rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-2 ${
                      isActive ? 'animate-pulse' : ''
                    }`}>
                      <step.icon className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-xs font-semibold text-slate-700 leading-tight">{step.label}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{step.sub}</p>
                  </div>
                  {/* Connector arrow */}
                  {i < PIPELINE_STEPS.length - 1 && (
                    <div className={`absolute top-1/3 -right-2 w-4 h-0.5 transition-colors duration-300 z-10 ${
                      isPast || isActive ? 'bg-violet-400' : 'bg-slate-200'
                    }`}>
                      <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent transition-colors duration-300 ${
                        isPast || isActive ? 'border-l-[5px] border-l-violet-400' : 'border-l-[5px] border-l-slate-200'
                      }`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Latency bar */}
          <div className="mt-6 flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-2">
            <Zap className="w-4 h-4 text-amber-500" />
            <div className="flex-1">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-500">Latenza media end-to-end</span>
                <span className="font-bold text-emerald-600">~1.8s</span>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full" style={{ width: '18%' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="metrics-section">
          {[
            { label: 'Chiamate Gestite', value: stats?.total_calls || 0, icon: Phone, color: 'text-sky-600', bg: 'bg-sky-50' },
            { label: 'Minuti AI Processati', value: stats?.total_minutes || 0, icon: Clock, color: 'text-violet-600', bg: 'bg-violet-50' },
            { label: 'Prenotazioni AI', value: stats?.ai_appointments || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Tasso Conversione', value: `${stats?.booking_rate || 0}%`, icon: TrendingUp, color: 'text-amber-600', bg: 'bg-amber-50' },
          ].map((m, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`metric-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 ${m.bg} rounded-lg flex items-center justify-center`}>
                  <m.icon className={`w-4 h-4 ${m.color}`} />
                </div>
                <span className="text-xs text-slate-500">{m.label}</span>
              </div>
              <p className={`text-2xl font-bold ${m.color}`}>{m.value}</p>
            </div>
          ))}
        </div>

        {/* Service Status */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="services-section">
          <h2 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <Server className="w-5 h-5 text-emerald-500" />
            Stato Servizi Cloud
          </h2>
          <p className="text-xs text-slate-400 mb-5">Monitoraggio in tempo reale di tutti i componenti infrastrutturali</p>

          <div className="space-y-3">
            {Object.entries(SERVICE_MAP).map(([key, svc]) => {
              const status = stats?.services_status?.[key] || 'operational';
              const isOp = status === 'operational';
              return (
                <div key={key} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`service-${key}`}>
                  <div className="w-10 h-10 bg-white rounded-lg border border-slate-200 flex items-center justify-center">
                    <svc.icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-slate-700">{svc.label}</p>
                    <p className="text-xs text-slate-400">{svc.desc}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${isOp ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                    <span className={`text-xs font-medium ${isOp ? 'text-emerald-600' : 'text-red-600'}`}>
                      {isOp ? 'Operativo' : 'Problema'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tech specs */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 text-white" data-testid="tech-specs">
          <h2 className="font-semibold mb-1 flex items-center gap-2">
            <Shield className="w-5 h-5 text-sky-400" />
            Specifiche Tecniche
          </h2>
          <p className="text-xs text-slate-400 mb-5">La stessa tecnologia AI utilizzata dalle aziende Fortune 500</p>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: 'Protocollo Audio', value: 'WebSocket Bidirezionale', detail: 'Streaming real-time a 8kHz' },
              { label: 'Codec', value: 'G.711 PCMU', detail: 'Standard telefonia mondiale' },
              { label: 'AI Modello', value: 'GPT-4o Series', detail: 'Ultima generazione OpenAI' },
              { label: 'STT Engine', value: 'Whisper Neural', detail: 'Precisione >95% in italiano' },
              { label: 'TTS Engine', value: 'Neural HD Italiano', detail: '7 voci naturali disponibili' },
              { label: 'Sicurezza', value: 'TLS 1.3 + AES-256', detail: 'Crittografia end-to-end' },
              { label: 'Uptime SLA', value: '99.9%', detail: 'Infrastruttura geo-ridondante' },
              { label: 'Scalabilità', value: 'Auto-scaling', detail: 'Gestione picchi automatica' },
              { label: 'Conformità', value: 'GDPR Ready', detail: 'Dati in EU, privacy by design' },
            ].map((spec, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">{spec.label}</p>
                <p className="text-sm font-bold text-white mt-0.5">{spec.value}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">{spec.detail}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </Layout>
  );
}
